import React, { useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Layout } from "../Layouts/Layout";
import { Campaign, CampaignState, CampaignType } from "../types";
import { CampaignCard } from "../Components/Campaign/Card";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router";
import {
  GET_SECTIONS,
  SectionsResult,
  GET_FORMS,
  FormsResult,
  GET_CAMPAIGNS,
  CampaignsResult,
  ADD_CAMPAIGN,
  UPDATE_CAMPAIGN,
  DELETE_CAMPAIGN,
  SEND_CAMPAIGN_REMINDERS,
} from "../Queries";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { sortByActivity } from "../Data";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { BlockingLoader } from "../Components/BlockingLoader";
import { CampaignState as CampaignStateType } from "../types";

const CampaignGroup = ({
  campaigns,
  label,
  states,
  onTransitionStart,
  onTransitionEnd,
}: {
  campaigns: Campaign[];
  label: string;
  states: CampaignState[];
  onTransitionStart?: (message: string) => void;
  onTransitionEnd?: () => void;
}) => {
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  //
  // Mutations
  //

  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.update"), error),
  });

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.delete"), error),
  });

  const [sendCampaignReminders] = useMutation(SEND_CAMPAIGN_REMINDERS, {
    refetchQueries: ["GetCampaign"],
    onError: (error) => setError(t("errors.campaigns.sendReminder"), error),
  });

  //
  // Event handlers
  //

  async function handleCampaignChange(
    changedCampaign: Campaign,
    activityLabel: string,
  ) {
    try {
      if (changedCampaign.state === CampaignStateType.Scheduled) {
        onTransitionStart?.(t("surveys.wait.scheduling") as string);
      }
      await updateCampaign({
        variables: {
          ...changedCampaign,
          activityLabel,
        },
      });
    } finally {
      onTransitionEnd?.();
    }
  }

  async function handleDelete(campaign: Campaign) {
    const confirmed = window.confirm(
      t("surveys.confirmDelete", { name: campaign.name }) as string,
    );
    if (!confirmed) {
      return;
    }

    await deleteCampaign({
      variables: {
        id: campaign.id,
      },
    });
  }

  async function handleRemind(campaign: Campaign) {
    const sent = await sendCampaignReminders({
      variables: {
        id: campaign.id,
      },
      context: {
        debounceKey: `campaign-${campaign.id}`,
      },
    });

    if (sent) {
      alert("Reminders sent successfully");
    } else {
      alert("No reminders to send");
    }
  }

  const filteredCampaigns = sortByActivity(
    campaigns,
    t("surveys.events.send"),
    states,
  );

  if (!filteredCampaigns || filteredCampaigns.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 6,
      }}
    >
      <Typography
        variant="h5"
        color="primary"
        sx={{ fontWeight: "bold", mb: 2 }}
      >
        {label}
      </Typography>

      <Grid container spacing={2}>
        {filteredCampaigns.map((campaign) => (
          <Grid item key={campaign.id} xs={12} md={6} lg={4}>
            <CampaignCard
              campaign={campaign}
              onChange={handleCampaignChange}
              onDelete={handleDelete}
              onRemind={handleRemind}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const Surveys = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();

  //
  // State
  //

  const [sectionId, setSectionId] = useState(undefined as string | undefined);
  const [addBusy, setAddBusy] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");

  //
  // Queries
  //
  const { loading: sectionsLoading, data: sectionsData } =
    useQuery<SectionsResult>(GET_SECTIONS, {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.sections.list"), error),
    });

  useQuery<FormsResult>(GET_FORMS, {
    variables: { filters: {} },
    onError: (error) => setError(t("errors.forms.list"), error),
  });

  const { loading: campaignsLoading, data: campaignsData } =
    useQuery<CampaignsResult>(GET_CAMPAIGNS, {
      variables: {
        filters: {
          sectionId: sectionId ? sectionId : undefined,
          campaignType: { exact: CampaignType.Survey },
        },
      },
      onError: (error) => setError(t("errors.campaigns.list"), error),
    });

  //
  // Mutations
  //

  const [addCampaign] = useMutation(ADD_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.create"), error),
  });

  const sections = sectionsData ? sectionsData.sections : undefined;
  const campaigns = campaignsData ? campaignsData.campaigns : [];
  const showLoading = sectionsLoading || sectionsLoading || campaignsLoading;

  if (!sectionsLoading && !sections) {
    throw new Error("No sections found");
  }

  //
  // Event handlers
  //

  async function handleAddCampaign() {
    const now = new Date();
    const dueDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      0,
      0,
    );

    setAddBusy(true);
    const { data: newCampaignResult } = await addCampaign({
      variables: {
        name: "New Survey",
        sectionId: sectionId,
        dueDate,
        campaignType: CampaignType.Survey,
      },
    });
    setAddBusy(false);

    if (!newCampaignResult?.addCampaign) {
      return;
    }
    const newCampaign = newCampaignResult.addCampaign;
    navigate(`/campaigns/${newCampaign.id}`);
  }

  //
  // Transition handlers
  //

  function handleTransitionStart(message: string) {
    setTransitionMessage(message);
    setIsTransitioning(true);
  }

  function handleTransitionEnd() {
    setIsTransitioning(false);
    setTransitionMessage("");
  }

  return (
    <Layout
      breadcrumbs={[{ title: t("navigation.surveys"), link: "/surveys" }]}
      loading={showLoading}
    >
      <BlockingLoader open={isTransitioning} message={transitionMessage} />
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <LoadingButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddCampaign}
            loading={addBusy}
          >
            {t("surveys.add")}
          </LoadingButton>
        </Box>

        <CampaignGroup
          campaigns={campaigns}
          label={t("surveys.groups.active")}
          states={[CampaignState.Active]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
        <CampaignGroup
          campaigns={campaigns}
          label={t("surveys.groups.scheduled")}
          states={[CampaignState.Scheduled]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
        <CampaignGroup
          campaigns={campaigns}
          label={t("surveys.groups.draft")}
          states={[CampaignState.Draft]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
        <CampaignGroup
          campaigns={campaigns}
          label={t("surveys.groups.closed")}
          states={[CampaignState.Closed]}
          onTransitionStart={handleTransitionStart}
          onTransitionEnd={handleTransitionEnd}
        />
      </Box>
    </Layout>
  );
};
